<template>
  <div :class="['share-view', { 'expand': viewMode === 1 }]" v-mdb-sticky="{ zIndex: 2, stickyTop: 40 }">
    <!-- mdbStickyが最初の子要素にtop: 40pxを付与するので緩衝材として空のdivを置いておく -->
    <div />
    <canvas
      :class="['board', drawTool]"
      ref="drawingArea"
      @mousedown="handleDown"
      @mouseup="handleUp"
      @mouseout="handleOut"
      @mousemove="handleMove"
      @touchstart.prevent="(e) => handleDown(e, true)"
      @touchmove.prevent="(e) => handleMove(e, true)"
      @touchend.prevent="(e) => handleUp(e, true)"
      :width="canvasSize.width"
      :height="canvasSize.height"
    />
    <div class="tools">
      <mdb-tooltip
        v-if="useBoard"
        trigger="hover"
        :options="{ placement: 'bottom' }"
        material
        sm
      >
        <span v-if="!device.isMobile" slot="tip">描画ツール設定</span>
        <mdb-btn
          slot="reference"
          tag="a"
          color="info"
          size="sm"
          class="btn-toggle-board-tools"
          floating
          @click.native="boardToolsModal = true"
        >
          <mdb-icon icon="edit" />
        </mdb-btn>
      </mdb-tooltip>
      <mdb-tooltip
        v-if="useBoard"
        trigger="hover"
        :options="{ placement: 'bottom' }"
        material
        sm
      >
        <span v-if="!device.isMobile" slot="tip">スナップショット</span>
        <mdb-btn
          slot="reference"
          tag="a"
          color="cyan"
          size="sm"
          class="btn-snapshot"
          floating
          @click.native="boardSnapshotModal = true"
        >
          <mdb-icon icon="camera" />
        </mdb-btn>
      </mdb-tooltip>
      <mdb-tooltip
        v-if="useBoard"
        trigger="hover"
        :options="{ placement: 'bottom' }"
        material
        sm
      >
        <span v-if="!device.isMobile" slot="tip">クリア</span>
        <mdb-btn
          slot="reference"
          tag="a"
          color="warning"
          size="sm"
          class="btn-clear-board"
          floating
          @click.native="boardClearModal = true"
        >
          <mdb-icon icon="trash-alt" />
        </mdb-btn>
      </mdb-tooltip>
      <mdb-tooltip
        trigger="hover"
        :options="{ placement: 'bottom' }"
        material
        sm
      >
        <span v-if="!device.isMobile" slot="tip">{{ viewMode === 1 ? '縮小' : '拡大' }}</span>
        <mdb-btn
          slot="reference"
          tag="a"
          class="btn-expand"
          size="sm"
          @click.native="() => $emit('onClickExpand')"
          floating
        >
          <mdb-icon :icon="viewMode === 1 ? 'compress' : 'expand'"/>
        </mdb-btn>
      </mdb-tooltip>
    </div>
    <video :srcObject.prop="shareStream" class="projector" autoplay muted playsinline ref="projector"></video>
    <audio :srcObject.prop="shareAudioStream" autoplay></audio>
    <Modal
      :modal="boardToolsModal"
      disableHeader
      disableCancelButton
      @close="boardToolsModal = false"
      @accept="boardToolsModal = false"
    >
      <h5 class="my-2">描画ツール設定</h5>
      <div class="mt-4">
        <mdb-form-inline>
          <custom-mdb-input type="radio" id="drawToolNone" label="なし" radioValue="none" v-model="drawTool"/>
          <custom-mdb-input type="radio" id="drawToolPen" label="ペン" radioValue="pencil" v-model="drawTool"/>
          <custom-mdb-input type="radio" id="drawToolEraser" label="消しゴム" radioValue="eraser" v-model="drawTool"/>
        </mdb-form-inline>
      </div>
      <div class="mt-4 font-weight-bold" v-if="drawTool === 'pencil'">
        <p>カラー</p>
        <color-picker v-model="pickerColor"/>
        <p class="mt-4">文字サイズ</p>
        <div
          class="stroke-size clickable"
          @click="toggleSize"
        >
        <span :class="`circle ${size}`" :style="`background-color: ${pickerColor.hex};`"/>
      </div>
      </div>
    </Modal>
    <Modal
      :modal="boardClearModal"
      title="確認"
      body="ボードをクリアしますか？"
      @close="boardClearModal = false"
      @accept="confirmedClearWhiteboard"
    />
    <Modal
      :modal="boardSnapshotModal"
      title="確認"
      body="ボードの内容を画像ファイル(png)で保存しますか？"
      @close="boardSnapshotModal = false"
      @accept="confirmedSnapshotWhiteboard"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { mdbSticky } from 'mdbvue'
import { Compact } from 'vue-color'
import Modal from '@/components/molecules/Modal'
import { WHITEBOARD_BASE_SIZE } from '@/lib/constants'

export default {
  name: 'ShareView',
  components: {
    Modal,
    'color-picker': Compact
  },
  directives: {
    mdbSticky
  },
  props: {
    shareState: Number,
    shareStream: MediaStream,
    shareAudioStream: MediaStream,
    viewMode: Number,
    useBoard: Boolean,
    device: Object
  },
  computed: {
    canvasSize: () => {
      return {
        width: WHITEBOARD_BASE_SIZE.width,
        height: WHITEBOARD_BASE_SIZE.height
      }
    },
    ...mapGetters({
      size: 'whiteboard/size',
      isDrawing: 'whiteboard/isDrawing',
      whiteboardTool: 'whiteboard/tool',
      drawingPosition: 'whiteboard/position',
      receivedData: 'whiteboard/receivedData',
      snapshotImage: 'whiteboard/snapshotImage'
    })
  },
  data() {
    return {
      boardToolsModal: false,
      boardClearModal: false,
      boardSnapshotModal: false,
      drawTool: 'none',
      pickerColor: '#FFFFFF'
    }
  },
  methods: {
    eventPosition: function(e, touch) {
      let x, y

      if (touch) {
        const canvasRect = this.$refs.drawingArea.getBoundingClientRect()
        x = e.changedTouches[0].pageX - canvasRect.left
        y = e.changedTouches[0].pageY - canvasRect.top
      } else {
        x = e.offsetX
        y = e.offsetY
      }

      return {
        x: x,
        y: y
      }
    },
    handleDown: function(e, touch) {
      if (e.button == 2 || this.drawTool === 'none') return
      const { x, y } = this.eventPosition(e, touch)

      this.setDrawing({
        isDrawing: true,
        x: x,
        y: y
      })
    },
    handleUp: function(e) {
      if (e.button == 2 || this.drawTool === 'none') return
      this.setDrawing({ isDrawing: false })
    },
    handleOut: function() {
      if (this.isDrawing) {
        this.setDrawing({ isDrawing: false })
      }
    },
    handleMove: function(e, touch) {
      if (!this.isDrawing) return
      const { x, y } = this.eventPosition(e, touch)

      this.setNewPosition({
        x: x,
        y: y
      })
      this.toolAction()
    },
    adjustMagnification: function() {
      setTimeout(() => {
        this.setMagnification({
          width: this.$refs.drawingArea.clientWidth,
          height: this.$refs.drawingArea.clientHeight
        })
      }, 0)
    },
    confirmedClearWhiteboard: function() {
      this.clearWhiteboard()
      this.boardClearModal = false
      this.$emit('onClearWhiteboard')
    },
    confirmedSnapshotWhiteboard: function() {
      this.snapshotWhiteboard({
        whiteboardCanvas: this.$refs.drawingArea,
        projector: this.$refs.projector
      })
      const virtualLink = document.createElement('a')
      virtualLink.href = this.snapshotImage
      virtualLink.download = `waaarp_snapshot.png`
      virtualLink.click()
      this.boardSnapshotModal = false
    },
    ...mapMutations({
      setContext: 'whiteboard/setContext',
      setColor: 'whiteboard/setColor',
      setTool: 'whiteboard/setTool',
      setMagnification: 'whiteboard/setMagnification',
      setDrawing: 'whiteboard/setDrawing',
      setNewPosition: 'whiteboard/setNewPosition',
      toggleSize: 'whiteboard/toggleSize',
      resetWhiteboardModule: 'whiteboard/resetModule'
    }),
    ...mapActions({
      toolAction: 'whiteboard/toolAction',
      remoteAction: 'whiteboard/remoteAction',
      snapshotWhiteboard: 'whiteboard/snapshot',
      clearWhiteboard: 'whiteboard/clear'
    })
  },
  mounted() {
    this.setContext(this.$refs.drawingArea.getContext('2d'))
    window.addEventListener('resize', this.adjustMagnification)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustMagnification)
  },
  watch: {
    shareState: function(val) {
      if (val === 0) {
        this.clearWhiteboard()
        this.resetWhiteboardModule()
        this.setContext(this.$refs.drawingArea.getContext('2d'))
        this.drawTool = 'none'
        this.pickerColor = '#FFFFFF'
      }
    },
    drawTool: function(val) {
      this.setTool(val)
    },
    pickerColor: function(val) {
      this.setColor(val.hex)
    },
    viewMode: function() {
      this.adjustMagnification()
    },
    receivedData: function(data) {
      if (data) this.remoteAction(data)
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";

.share-view {
  margin: 5px;
  position: relative;
  width: calc(100% - 10px);
  box-shadow: 0 5px 12px 2px rgba(17,17,17,0.5);

  &:before {
    content: "";
    display: block;
    padding-top: 56.25%;
  }

  &.expand {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    box-shadow: none;

    .board,.projector {
      top: calc(50vh - 50%);
      box-shadow: 0 5px 12px 2px rgba(17,17,17,0.5);
    }
  }

  .board,.projector,.tools {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }

  .tools {
    background-color: transparent;
    z-index: 1;
    pointer-events: none;
  }

  .board {
    background-color: transparent;
    z-index: 1;

    &.eraser {
      cursor: url('/cursor/eraser.png') 1 15, crosshair;
    }

    &.pencil {
      cursor: url('/cursor/pen.png') 1 15, crosshair;
    }
  }

  .projector {
    background-color: $secondary-color;
    z-index: -1;
  }

  .btn-expand, .btn-toggle-board-tools, .btn-clear-board, .btn-snapshot {
    position: absolute;
    right: 0;
    top: 0;
    opacity: 1;
    pointer-events: all;

    &:hover {
      opacity: 1;
    }
  }

  .btn-toggle-board-tools {
    margin-right: 145px;
  }

  .btn-snapshot {
    margin-right: 100px;
  }

  .btn-clear-board {
    margin-right: 55px;
  }

  .stroke-size {
    width: 30px;
    border-radius: 10%;
    box-shadow: -5px 5px 12px -2px rgba(17, 17, 17, 0.5);
    background: transparent;
    position: relative;

    &:before {
      content: "";
      display: block;
      padding-top: 100%;
    }

    .circle {
      position: absolute;
      border-radius: 50%;
      border: 1px solid lightGray;

      &.sm {
        top: 35%;
        left: 35%;
        width: 30%;
        height: 30%;
      }

      &.md {
        top: 25%;
        left: 25%;
        width: 50%;
        height: 50%;
      }

      &.lg {
        top: 15%;
        left: 15%;
        width: 70%;
        height: 70%;
      }
    }
  }
}
</style>
